import React from "react";
import { Box, Button, Typography } from "@mui/material";
import type { AppDataGridPagination } from "./types";
import styled from "styled-components";
import isMobile from "is-mobile";

// --- Styled Components para o Select customizado ---
const SelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  font-family: "Roboto", sans-serif;
`;

const StyledSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 8px;
  padding: 8px 32px 8px 12px;
  font-size: 14px;
  color: #3c4043;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #5f6368;
  }

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #5f6368;
`;

interface AppDataGridFooterProps {
  pagination: AppDataGridPagination;
  tableLabel: string;
  pageSizeOptions?: number[];
}

export const AppDataGridFooter: React.FC<AppDataGridFooterProps> = ({
  pagination,
  tableLabel,
  pageSizeOptions = [20, 50, 100],
}) => {
  const { page, pageSize, totalCount, onPageChange, onPageSizeChange } =
    pagination;

  const start = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(page * pageSize, totalCount);
  const totalPages = Math.ceil(totalCount / pageSize);
  const isLastPage = page >= totalPages || !Number.isFinite(totalPages);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" }, // Empilha em telas extra pequenas
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
        gap: { xs: 2, sm: 0 }, // Adiciona espaço quando empilhado
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ display: { xs: "none", md: "block" } }} // Oculta em telas pequenas
      >
        Exibindo {start} a {end} de {totalCount} {tableLabel}
      </Typography>

      <Box
        sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}
      >
        <SelectWrapper>
          <StyledSelect
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </StyledSelect>
          <SelectArrow />
        </SelectWrapper>

        <Button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          sx={{ minWidth: { xs: "40px", sm: "auto" } }}
        >
          {isMobile() ? "«" : "Anterior"}
        </Button>

        {/* Mobile View: Show current page button only */}
        <Button
          variant="contained"
          disableElevation
          sx={{
            pointerEvents: "none",
            display: { xs: "inline-flex", md: "none" },
          }}
        >
          {page}
        </Button>

        {/* Desktop View: Show list of pages */}
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
          {(() => {
            const maxPagesToShow = 10;
            let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
            let endPage = startPage + maxPagesToShow - 1;

            if (endPage > totalPages) {
              endPage = totalPages;
              startPage = Math.max(1, endPage - maxPagesToShow + 1);
            }

            // Ensure startPage is at least 1
            startPage = Math.max(1, startPage);

            const pages = [];
            for (let i = startPage; i <= endPage; i++) {
              pages.push(i);
            }

            return pages.map((p) => (
              <Button
                key={p}
                variant={p === page ? "contained" : "outlined"}
                disableElevation
                onClick={() => onPageChange(p)}
                sx={{
                  minWidth: "32px",
                  height: "32px",
                  p: 0,
                  borderColor:
                    p === page ? "primary.main" : "rgba(0, 0, 0, 0.23)",
                  color: p === page ? "primary.contrastText" : "text.primary",
                }}
              >
                {p}
              </Button>
            ));
          })()}
        </Box>

        <Button
          onClick={() => onPageChange(page + 1)}
          disabled={isLastPage}
          sx={{ minWidth: { xs: "40px", sm: "auto" } }}
        >
          {isMobile() ? "»" : "Próximo"}
        </Button>
      </Box>
    </Box>
  );
};
